export const getCatId = (category) =>
  String(category?.Id ?? category?.id ?? category?.slug ?? "");

export const getCatSlug = (category) => category?.slug ?? "";

export const getCatParentId = (category) =>
  String(category?.parentId ?? category?.FatherId ?? "ROOT");

export const getCatName = (category) =>
  (category?.name ?? category?.Name ?? "").toLowerCase();

/** Flattens the catalog tree (roots + nested `children`) into one list. */
export const flattenCategoryTree = (categories) => {
  const flat = [];
  const seen = new Set();

  const visit = (nodes) => {
    if (!Array.isArray(nodes)) return;
    nodes.forEach((node) => {
      const key = getCatSlug(node) || getCatId(node);
      if (key && !seen.has(key)) {
        seen.add(key);
        flat.push(node);
      }
      if (Array.isArray(node.children)) {
        visit(node.children);
      }
    });
  };

  visit(categories);
  return flat;
};

export const isRootParent = (parentId) =>
  parentId === "ROOT" ||
  parentId === "null" ||
  parentId === "" ||
  parentId == null;

export const isAtRootLevel = (category) => {
  const rawParent = category?.parentId ?? category?.FatherId;
  if (rawParent === "ROOT" || rawParent === "" || rawParent === "null") {
    return true;
  }
  const parentSlug = category?.parentSlug;
  return parentSlug === "_root" || parentSlug === "ROOT";
};

export const isOrphanCategory = (category) => {
  const rawParent = category?.parentId ?? category?.FatherId;
  if (rawParent !== null) return false;
  return !isAtRootLevel(category);
};

const resolveCategory = (key, byId, bySlug) => {
  if (!key) return null;
  return byId.get(key) || bySlug.get(key) || null;
};

const registerVisible = (visibleIds, category) => {
  const id = getCatId(category);
  const slug = getCatSlug(category);
  if (id) visibleIds.add(id);
  if (slug) visibleIds.add(slug);
};

const buildChildrenByParent = (categories, byId, bySlug) => {
  const childrenByParent = new Map();

  const addChild = (parentKey, child) => {
    if (!parentKey) return;
    if (!childrenByParent.has(parentKey)) {
      childrenByParent.set(parentKey, []);
    }
    childrenByParent.get(parentKey).push(child);
  };

  categories.forEach((category) => {
    const parentKey = getCatParentId(category);
    addChild(parentKey, category);

    const parent = resolveCategory(parentKey, byId, bySlug);
    if (parent) {
      addChild(getCatId(parent), category);
      const parentSlug = getCatSlug(parent);
      if (parentSlug) addChild(parentSlug, category);
    }
  });

  return childrenByParent;
};

const addAncestors = (visibleIds, categoryId, byId, bySlug) => {
  let current = resolveCategory(categoryId, byId, bySlug);
  while (current) {
    registerVisible(visibleIds, current);
    const parentKey = getCatParentId(current);
    if (isRootParent(parentKey)) break;
    current = resolveCategory(parentKey, byId, bySlug);
    if (!current) break;
  }
};

const addDescendants = (
  visibleIds,
  startCategory,
  childrenByParent,
  byId,
  bySlug,
) => {
  const queue = [getCatId(startCategory), getCatSlug(startCategory)].filter(
    Boolean,
  );
  const visited = new Set();

  while (queue.length) {
    const key = queue.shift();
    if (!key || visited.has(key)) continue;
    visited.add(key);

    const current = resolveCategory(key, byId, bySlug) || startCategory;
    registerVisible(visibleIds, current);

    const children = childrenByParent.get(key) || [];
    children.forEach((child) => {
      registerVisible(visibleIds, child);
      const childId = getCatId(child);
      const childSlug = getCatSlug(child);
      if (childId && !visited.has(childId)) queue.push(childId);
      if (childSlug && !visited.has(childSlug)) queue.push(childSlug);
    });
  }
};

const addVisiblePath = (
  visibleIds,
  category,
  byId,
  bySlug,
  childrenByParent,
) => {
  const categoryKey = getCatId(category) || getCatSlug(category);
  addAncestors(visibleIds, categoryKey, byId, bySlug);
  addDescendants(visibleIds, category, childrenByParent, byId, bySlug);
};

export const isInSearchVisible = (visibleIds, category) => {
  if (!visibleIds?.size) return true;
  const id = getCatId(category);
  const slug = getCatSlug(category);
  return (id && visibleIds.has(id)) || (slug && visibleIds.has(slug));
};

export const childBelongsToParent = (child, parent) => {
  const childParent = getCatParentId(child);
  const parentId = getCatId(parent);
  const parentSlug = getCatSlug(parent);
  return childParent === parentId || (parentSlug && childParent === parentSlug);
};

/**
 * Builds ids/slugs to show when searching the department tree.
 * Scans the full tree (including nested children) and resolves parentId as slug or id.
 */
export const buildSearchVisibleIds = (
  categories,
  { query = "", slugMatches = [], activeOnly = true } = {},
) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery && slugMatches.length === 0) {
    return null;
  }

  const flatCategories = flattenCategoryTree(categories);
  const visibleIds = new Set();
  const byId = new Map();
  const bySlug = new Map();

  flatCategories.forEach((category) => {
    const id = getCatId(category);
    const slug = getCatSlug(category);
    if (id) byId.set(id, category);
    if (slug) bySlug.set(slug, category);
  });

  const childrenByParent = buildChildrenByParent(flatCategories, byId, bySlug);

  if (normalizedQuery) {
    flatCategories.forEach((category) => {
      if (activeOnly && category.status !== "ACTIVE") return;
      if (!getCatName(category).includes(normalizedQuery)) return;
      addVisiblePath(visibleIds, category, byId, bySlug, childrenByParent);
    });
  }

  slugMatches.forEach((slug) => {
    const category = bySlug.get(slug);
    if (!category) return;
    if (activeOnly && category.status !== "ACTIVE") return;
    addVisiblePath(visibleIds, category, byId, bySlug, childrenByParent);
  });

  return visibleIds;
};

export const extractCategorySlugsFromProducts = (products) => {
  const slugs = new Set();
  products.forEach((product) => {
    const hierarchy = product?.categories?.[0];
    if (!Array.isArray(hierarchy)) return;
    hierarchy.forEach((node) => {
      if (node?.slug) slugs.add(node.slug);
    });
  });
  return [...slugs];
};
